import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Upload, Button, Steps, Table, Tabs, Tag, Alert, Progress,
    Row, Col, Divider, Modal, Tooltip, Badge, Result, Typography,
    Select, Space
} from 'antd';
import {
    InboxOutlined, CheckCircleOutlined, WarningOutlined,
    FileExcelOutlined, CloudUploadOutlined, BarChartOutlined,
    DownloadOutlined, ReloadOutlined, ExclamationCircleOutlined,
    FolderOpenOutlined, GlobalOutlined, SendOutlined,
    SyncOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { XLSX } from '../../utils/xlsxShim';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import { libellesTraduits } from '../../i18n/libelles';
import RecalculStatutsButton from './RecalculStatutsButton';
import PageHeader from '../../components/PageHeader';

const { Dragger } = Upload;
const { Title, Text } = Typography;

//  Constantes de mapping 

const DOM_MAP = {
    // Format 2025 - codes longs
    DCM: 'MIXTE', DM: 'MATERIEL', DC: 'CORPOREL',
    DMC: 'MIXTE', DMCM: 'MIXTE', FM: 'MATERIEL', R: 'MATERIEL',
    // Commun aux deux formats
    CM: 'MIXTE',
    // Format 2022 - codes courts (C=Corporel, M=Matériel, CM=Mixte)
    C: 'CORPOREL', M: 'MATERIEL',
};

const DOM_COLOR = { MIXTE: 'volcano', MATERIEL: 'orange', CORPOREL: 'red' };

const PAYS_LIBELLE = libellesTraduits('reprise:pays');

// Avertissements de lecture : produits au moment du dépôt du fichier, dans la langue de l'interface.
const avert = (cle, options) => i18n.t(`reprise:avertissements.${cle}`, options);

//  Helpers partagés 

/**
 * Parse une date Excel et la normalise en ISO yyyy-MM-dd.
 * Accepte : objet Date, numéro de série Excel, ou chaîne dans l'un des
 * formats : ISO (yyyy-MM-dd[Thh:mm:ss]), jj/mm/aaaa, jj-mm-aaaa, jj.mm.aaaa,
 * yyyy/mm/dd, jj/mm/aa. Cellule vide ou format inconnu → null.
 */
function parseDateExcel(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    const pad = (n) => String(n).padStart(2, '0');

    // Objet Date (cellDates: true côté XLSX)
    if (raw instanceof Date) {
        if (isNaN(raw.getTime())) return null;
        return `${raw.getFullYear()}-${pad(raw.getMonth() + 1)}-${pad(raw.getDate())}`;
    }

    // Numéro de série Excel
    if (typeof raw === 'number') {
        const d = XLSX.SSF.parse_date_code(raw);
        return d && d.y ? `${d.y}-${pad(d.m)}-${pad(d.d)}` : null;
    }

    if (typeof raw !== 'string') return null;

    // Normalisation : NBSP, tabs, espaces multiples, espaces autour des séparateurs
    const s = raw
        .replace(/ /g, ' ')
        .replace(/\t/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/\s*([/.\-])\s*/g, '$1')
        .trim();
    if (!s) return null;

    // ISO datetime → on garde la partie date
    if (s.includes('T')) {
        const iso = s.split('T')[0];
        if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(iso)) {
            const [y, m, d] = iso.split('-');
            return `${y}-${pad(+m)}-${pad(+d)}`;
        }
    }

    // yyyy[-/.]mm[-/.]dd
    let m = s.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
    if (m) return `${m[1]}-${pad(+m[2])}-${pad(+m[3])}`;

    // jj[-/.]mm[-/.]aa(aa) - format français BNCB
    m = s.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{2,4})$/);
    if (m) {
        let y = parseInt(m[3], 10);
        if (y < 100) y += y < 50 ? 2000 : 1900;
        return `${y}-${pad(+m[2])}-${pad(+m[1])}`;
    }

    return null;
}

/**
 * Le format dépend de l'année des données, pas de la structure du fichier :
 *   annee <= 2022 → V2022  (ligne d'en-têtes, N° SIN MANUEL complet en col C)
 *   annee >= 2023 → V2025  (pas d'en-têtes, année/séq/pays en colonnes séparées)
 */
function estFormatV2022(anneeRef) {
    return parseInt(anneeRef, 10) <= 2022;
}

//  Parseurs Excel -

/**
 * Parse la feuille ET (Étrangers au Togo).
 *
 * FORMAT V2025 - pas d'en-têtes, données dès L1 :
 *   A(0)=N°  B(1)=Date  C(2)=Année  D(3)=Séq  E(4)=Pays
 *   F(5)=EcarteBrune  G(6)=Homologue  H(7)=Assureur  I(8)=N°Police
 *   J(9)=Assuré  K(10)=Dommage  L(11)=Immat  O(14)=PaysGest  P(15)=RED
 *
 * FORMAT V2022 - L1=entêtes, données à partir de L2 :
 *   A(0)=N°ordre  B(1)=Date  C(2)=N°Manuel(complet)  D(3)=N°Auto
 *   E(4)=Homologue  F(5)=Assureur  G(6)=N°Compagnies  H(7)=Assuré
 *   I(8)=Dommage(C/CM/M)  J(9)=Immat  K(10)=N°Police  L(11)=CarteBrune
 *   M(12)=Emetteur(code pays)  N(13)=RED
 */
function parseET(ws, anneeRef) {
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, cellDates: true });
    const v2022 = estFormatV2022(anneeRef);
    const data = [];

    for (let i = 1; i < rows.length; i++) {
        const r = rows[i];

        if (v2022) {
            //  Format 2022 
            const numManuel = String(r[2] || '').trim();
            if (!numManuel) continue;

            // Construire numeroSinistreLocal (4 digits) depuis le numéro complet
            // Ex: "2022/001/BJ" → "2022/0001/BJ"
            const parts = numManuel.split('/');
            const numLocal = parts.length === 3
                ? `${parts[0]}/${parts[1].padStart(4, '0')}/${parts[2]}`
                : numManuel;

            const pays = String(r[12] || '').trim().toUpperCase(); // EMETTEUR
            const dom = String(r[8] || '').trim().toUpperCase(); // DOMMAGES

            data.push({
                _ligne: i + 1, _type: 'ET', _format: 'V2022',
                numeroSinistreManuel: numManuel,
                numeroSinistreLocal: numLocal,
                numeroSinistreEcarteBrune: String(r[3] || '').trim(), // N°SIN AUTO
                numeroSinistreHomologue: String(r[4] || '').trim(),
                dateAccident: parseDateExcel(r[1]),
                typeSinistre: 'SURVENU_TOGO',
                typeDommage: DOM_MAP[dom] || 'MATERIEL',
                typeDommageRaw: dom,
                assureNomComplet: String(r[7] || '').trim(),
                assureImmatriculation: String(r[9] || '').trim(),
                assureurDeclarant: String(r[5] || '').trim(),
                numeroPoliceAssureur: String(r[6] || '').trim(),
                paysEmetteurCode: pays, // EMETTEUR = pays étranger
                paysGestionnaireCode: 'TG',
                redacteur: String(r[13] || '').trim(),
                _warnings: [],
            });

        } else {
            //  Format 2025 
            if (!r[0] || String(r[0]).startsWith('=')) continue;
            const seq3 = String(r[3] || '').padStart(3, '0');
            const seq4 = String(r[3] || '').padStart(4, '0');
            const annee = String(r[2] || anneeRef);
            const pays = String(r[4] || '').trim().toUpperCase();
            const dom = String(r[10] || '').trim().toUpperCase();

            data.push({
                _ligne: i + 1, _type: 'ET', _format: 'V2025',
                numeroSinistreManuel: `${annee}/${seq3}/${pays}`,
                numeroSinistreLocal: `${annee}/${seq4}/${pays}`,
                numeroSinistreEcarteBrune: String(r[5] || '').trim(),
                numeroSinistreHomologue: String(r[6] || '').trim(),
                dateAccident: parseDateExcel(r[1]),
                typeSinistre: 'SURVENU_TOGO',
                typeDommage: DOM_MAP[dom] || 'MATERIEL',
                typeDommageRaw: dom,
                assureNomComplet: String(r[9] || '').trim(),
                assureImmatriculation: String(r[11] || '').trim(),
                assureurDeclarant: String(r[7] || '').trim(),
                numeroPoliceAssureur: String(r[8] || '').trim(),
                paysEmetteurCode: pays,
                paysGestionnaireCode: 'TG',
                redacteur: String(r[15] || '').trim(),
                _warnings: [],
            });
        }
    }
    return valider(data);
}

/**
 * Parse la feuille TE (Togolais à l'Étranger).
 *
 * FORMAT V2025 - pas d'en-têtes, données dès L1 :
 *   A(0)=N°  B(1)=Date  C(2)=Préfixe  D(3)=Séq  E(4)=Pays  F(5)=Année
 *   G(6)=EcarteBrune  H(7)=Homologue  I(8)=Assureur  J(9)=N°Police
 *   K(10)=Assuré  L(11)=Dommage  M(12)=Immat  P(15)=PaysGest  Q(16)=RED
 *
 * FORMAT V2022 - L1=entêtes, données à partir de L2 :
 *   A(0)=N°ordre  B(1)=Date  C(2)=N°Manuel("FI 001/BF/2022")  D(3)=N°Auto
 *   E(4)=Homologue  F(5)=Assureur  G(6)=N°Compagnies  H(7)=Assuré
 *   I(8)=Dommage(C/CM/M)  J(9)=Immat  K(10)=N°Police  L(11)=CarteBrune
 *   M(12)=Gestionnaire(pays étranger)  N(13)=RED
 */
function parseTE(ws, anneeRef) {
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, cellDates: true });
    const v2022 = estFormatV2022(anneeRef);
    const data = [];

    for (let i = 1; i < rows.length; i++) {
        const r = rows[i];

        if (v2022) {
            //  Format 2022 
            const numManuel = String(r[2] || '').trim();
            if (!numManuel) continue;

            // Construire numeroSinistreLocal avec slashes et séquentiel 4 chiffres
            // Ex: "FI 001/BF/2022" → "FI/0001/BF/2022"
            let numLocal = numManuel;
            const spaceIdx = numManuel.indexOf(' ');
            if (spaceIdx > 0) {
                const prefixe = numManuel.substring(0, spaceIdx);         // "FI"
                const reste = numManuel.substring(spaceIdx + 1);        // "001/BF/2022"
                const parts = reste.split('/');                          // ["001","BF","2022"]
                if (parts.length === 3) {
                    numLocal = `${prefixe}/${parts[0].padStart(4, '0')}/${parts[1]}/${parts[2]}`;
                }
            }

            const paysGest = String(r[12] || '').trim().toUpperCase(); // GESTIONNAIRE
            const dom = String(r[8] || '').trim().toUpperCase(); // DOMMAGES

            data.push({
                _ligne: i + 1, _type: 'TE', _format: 'V2022',
                numeroSinistreManuel: numManuel,
                numeroSinistreLocal: numLocal,
                numeroSinistreEcarteBrune: String(r[3] || '').trim(), // N°SIN AUTO
                numeroSinistreHomologue: String(r[4] || '').trim(),
                dateAccident: parseDateExcel(r[1]),
                typeSinistre: 'SURVENU_ETRANGER',
                typeDommage: DOM_MAP[dom] || 'MATERIEL',
                typeDommageRaw: dom,
                assureNomComplet: String(r[7] || '').trim(),
                assureImmatriculation: String(r[9] || '').trim(),
                assureurDeclarant: String(r[5] || '').trim(),
                numeroPoliceAssureur: String(r[6] || '').trim(),
                paysEmetteurCode: 'TG',       // toujours TG pour un TE
                paysGestionnaireCode: paysGest,  // pays étranger (col GESTIONNAIRE)
                redacteur: String(r[13] || '').trim(),
                _warnings: [],
            });

        } else {
            //  Format 2025 
            if (!r[0] || String(r[0]).startsWith('=')) {
                if (!r[2] && !r[6]) continue;
            }
            const prefixe = String(r[2] || '').trim().toUpperCase();
            const seq3 = String(r[3] || '').padStart(3, '0');
            const seq4 = String(r[3] || '').padStart(4, '0');
            const pays = String(r[4] || '').trim().toUpperCase();
            const annee = String(r[5] || anneeRef);
            const dom = String(r[11] || '').trim().toUpperCase();

            data.push({
                _ligne: i + 1, _type: 'TE', _format: 'V2025',
                numeroSinistreManuel: `${prefixe}/${seq3}/${pays}/${annee}`,
                numeroSinistreLocal: `${prefixe}/${seq4}/${pays}/${annee}`,
                numeroSinistreEcarteBrune: String(r[6] || '').trim(),
                numeroSinistreHomologue: String(r[7] || '').trim(),
                dateAccident: parseDateExcel(r[1]),
                typeSinistre: 'SURVENU_ETRANGER',
                typeDommage: DOM_MAP[dom] || 'MATERIEL',
                typeDommageRaw: dom,
                assureNomComplet: String(r[10] || '').trim(),
                assureImmatriculation: String(r[12] || '').trim(),
                assureurDeclarant: String(r[8] || '').trim(),
                numeroPoliceAssureur: String(r[9] || '').trim(),
                paysEmetteurCode: 'TG',
                paysGestionnaireCode: pays,
                redacteur: String(r[16] || '').trim(),
                _warnings: [],
            });
        }
    }
    return valider(data);
}


function valider(data) {
    return data.map(r => {
        const warnings = [];
        if (!r.dateAccident) warnings.push(avert('dateManquante'));
        if (!r.assureNomComplet) warnings.push(avert('assureManquant'));
        if (!r.paysEmetteurCode) warnings.push(avert('paysEmetteurManquant'));
        if (!DOM_MAP[r.typeDommageRaw]) warnings.push(avert('dommageInconnu', { type: r.typeDommageRaw }));
        if (!r.numeroSinistreEcarteBrune) warnings.push(avert('numeroAutomatiqueManquant'));
        return { ...r, _warnings: warnings, _hasWarning: warnings.length > 0 };
    });
}

//  Parseur compagnies -

function parseCompagnies(wb) {
    const list = [];
    let currentPays = null;
    for (const sheetName of wb.SheetNames) {
        const ws = wb.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
        for (const r of rows) {
            if (!r[1] && !r[0]) continue;
            // Feuil1: [PAYS, COMPAGNIE, CODE]
            // Feuil2: [NOM, CODE]
            if (sheetName === 'Feuil1') {
                if (r[0] && r[0] !== 'PAYS' && r[0] !== "COMPAGNIES D'ASSURANCE DE LA ZONE CEDEAO") {
                    currentPays = String(r[0]).trim();
                }
                if (r[1] && r[2] && r[1] !== 'COMPAGNIE') {
                    list.push({ raisonSociale: String(r[1]).trim(), code: String(r[2]).trim(), pays: currentPays });
                }
            } else if (sheetName === 'Feuil2') {
                if (r[0] && r[1] && r[0] !== 'NOM') {
                    list.push({ raisonSociale: String(r[0]).trim(), code: String(r[1]).trim(), pays: null });
                }
            }
        }
    }
    return list;
}

//  Colonnes tableau preview -

const COLS_PREVIEW = (t) => [
    {
        title: '', key: 'status', width: 36, fixed: 'left',
        render: (_, r) => r._hasWarning
            ? <Tooltip title={r._warnings.join(' | ')}><WarningOutlined style={{ color: '#f97316' }} /></Tooltip>
            : <CheckCircleOutlined style={{ color: '#1a6b3c' }} />,
    },
    {
        title: t('sinistres.colonnes.numeroManuel'), dataIndex: 'numeroSinistreManuel', key: 'man', width: 140, fixed: 'left',
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c', fontSize: 12 }}>{v}</span>
    },
    {
        title: t('sinistres.colonnes.numeroLocal'), dataIndex: 'numeroSinistreLocal', key: 'loc', width: 150,
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontSize: 11, color: '#374151' }}>{v}</span>
    },
    {
        title: t('sinistres.colonnes.numeroAutomatique'), dataIndex: 'numeroSinistreEcarteBrune', key: 'auto', width: 190,
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontSize: 11 }}>{v}</span>
    },
    {
        title: t('sinistres.colonnes.numeroHomologue'), dataIndex: 'numeroSinistreHomologue', key: 'hom', width: 150,
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontSize: 11 }}>{v}</span>
    },
    {
        title: t('sinistres.colonnes.dateAccident'), dataIndex: 'dateAccident', key: 'date', width: 110,
        render: v => v ? new Date(v).toLocaleDateString(localeIntl()) : <Tag color="red">{t('commun.manquant')}</Tag>
    },
    { title: t('commun.colonnes.assure'), dataIndex: 'assureNomComplet', key: 'assure', ellipsis: true, width: 180 },
    {
        title: t('sinistres.colonnes.immatriculation'), dataIndex: 'assureImmatriculation', key: 'immat', width: 140,
        render: v => <span style={{ fontFamily: 'var(--font-main)', background: '#f4f6f5', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>{v}</span>
    },
    {
        title: t('sinistres.colonnes.dommage'), dataIndex: 'typeDommage', key: 'dom', width: 100,
        render: (v, r) => <Tag color={DOM_COLOR[v] || 'default'} style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>
    },
    {
        title: (
            <Tooltip title={t('sinistres.colonnes.aidePaysGestionnaire')}>
                <span>{t('sinistres.colonnes.paysGestionnaire')}</span>
            </Tooltip>
        ),
        dataIndex: 'paysGestionnaireCode', key: 'paysG', width: 115,
        render: (v, r) => (
            <Tag color={v === 'TG' ? 'green' : 'volcano'} style={{ borderRadius: 20 }}>
                {v || '-'}
            </Tag>
        ),
    },
    {
        title: (
            <Tooltip title={t('sinistres.colonnes.aidePaysEmetteur')}>
                <span>{t('sinistres.colonnes.paysEmetteur')}</span>
            </Tooltip>
        ),
        dataIndex: 'paysEmetteurCode', key: 'paysE', width: 110,
        render: v => <Tag color={v === 'TG' ? 'green' : 'geekblue'} style={{ borderRadius: 20 }}>{v || '-'}</Tag>
    },
    { title: t('sinistres.colonnes.assureur'), dataIndex: 'assureurDeclarant', key: 'ass', width: 140, ellipsis: true },
];

// ══════════════════════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function RepriseSinistres() {
    const { t } = useTranslation('reprise');
    const [step, setStep] = useState(0);
    const [anneeSelectionnee, setAnneeSelectionnee] = useState(new Date().getFullYear());
    const [fileName, setFileName] = useState('');
    const [dataET, setDataET] = useState([]);
    const [dataTE, setDataTE] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [activeTab, setActiveTab] = useState('ET');
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [rapport, setRapport] = useState(null);
    const [showCompagniesModal, setShowCompagniesModal] = useState(false);
    const [importantCompagnies, setImportingCompagnies] = useState(false);
    const [rapportCompagnies, setRapportCompagnies] = useState(null);
    //  Loader parsing fichier 
    const [parsingFile, setParsingFile] = useState(false);
    const [fakeProgress, setFakeProgress] = useState(0);
    const fakeTimerRef = useRef(null);

    // Anime la barre de progression pendant le parsing
    useEffect(() => {
        if (parsingFile) {
            setFakeProgress(0);
            let p = 0;
            fakeTimerRef.current = setInterval(() => {
                // Monte rapidement jusqu'à 85%, puis ralentit (le vrai parsing prend du temps)
                p += p < 60 ? 8 : p < 80 ? 3 : 0.5;
                if (p >= 88) p = 88;
                setFakeProgress(Math.round(p));
            }, 120);
        } else {
            clearInterval(fakeTimerRef.current);
            setFakeProgress(100);
        }
        return () => clearInterval(fakeTimerRef.current);
    }, [parsingFile]);

    //  Lecture Excel 
    const onExcelDrop = (file) => {
        setFileName(file.name);
        setParsingFile(true); // afficher le loader immédiatement

        const reader = new FileReader();
        reader.onload = (e) => {
            // On défère le parsing (XLSX.read + parseET/parseTE)
            // pour laisser React peindre l'overlay avant de bloquer le thread principal.
            setTimeout(async () => {
                try {
                    const wb = await XLSX.read(e.target.result, { type: 'array', cellDates: true });

                    // Détection générique des feuilles ET et TE :
                    // On cherche d'abord "ET {annee}" puis toute feuille dont le nom commence par "ET"
                    const etName = wb.SheetNames.find(n => n.trim().toUpperCase() === `ET ${anneeSelectionnee}`)
                        || wb.SheetNames.find(n => n.trim().toUpperCase().startsWith('ET'));
                    const teName = wb.SheetNames.find(n => n.trim().toUpperCase() === `TE ${anneeSelectionnee}`)
                        || wb.SheetNames.find(n => n.trim().toUpperCase().startsWith('TE'));

                    if (!etName || !teName) {
                        toast.error(
                            t('sinistres.feuillesIntrouvables', { annee: anneeSelectionnee, feuilles: wb.SheetNames.join(', ') })
                        );
                        return;
                    }

                    const et = parseET(wb.Sheets[etName], anneeSelectionnee);
                    const te = parseTE(wb.Sheets[teName], anneeSelectionnee);
                    if (wb.SheetNames.includes('Feuil1')) setCompagnies(parseCompagnies(wb));
                    setDataET(et);
                    setDataTE(te);
                    setStep(1);
                    toast.success(t('sinistres.feuillesChargees', { feuilleEt: etName, nombreEt: et.length, feuilleTe: teName, nombreTe: te.length }));
                } catch (err) {
                    toast.error(t('commun.erreurLectureExcel', { message: err.message }));
                } finally {
                    setParsingFile(false);
                }
            }, 50);
        };
        reader.onerror = () => {
            toast.error(t('sinistres.erreurLectureFichier'));
            setParsingFile(false);
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    const onCompagniesDrop = (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const wb = await XLSX.read(e.target.result, { type: 'array' });
                setCompagnies(parseCompagnies(wb));
                toast.success(t('sinistres.compagniesChargees', { nombre: parseCompagnies(wb).length }));
            } catch (err) {
                toast.error(t('sinistres.erreurLectureCompagnies', { message: err.message }));
            }
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    //  Stats 
    // Répartition par pays ÉTRANGER :
    //   ET → paysEmetteurCode (le véhicule étranger)
    //   TE → paysGestionnaireCode (le pays où l'accident a eu lieu)
    const stats = useMemo(() => {
        const all = [...dataET, ...dataTE];
        const warnings = all.filter(r => r._hasWarning);
        const byPays = {};
        dataET.forEach(r => {
            const k = r.paysEmetteurCode; if (k) byPays[k] = (byPays[k] || 0) + 1;
        });
        dataTE.forEach(r => {
            const k = r.paysGestionnaireCode; if (k) byPays[k] = (byPays[k] || 0) + 1;
        });
        return {
            totalET: dataET.length, totalTE: dataTE.length, total: all.length,
            warnings: warnings.length, valid: all.length - warnings.length,
            byPays,
        };
    }, [dataET, dataTE]);

    //  Import sinistres 
    const lancerImport = async () => {
        setImporting(true);
        setImportProgress(0);
        const BATCH = 50;
        const all = [...dataET, ...dataTE];
        let importes = 0, erreurs = 0, doublons = 0;
        const details = [];
        const lignesOk = [];

        // Envoyer par batch
        for (let i = 0; i < all.length; i += BATCH) {
            const batch = all.slice(i, i + BATCH);
            try {
                const res = await axiosInstance.post('/v1/reprise/sinistres', {
                    sinistres: batch.map(r => ({
                        numeroSinistreManuel: r.numeroSinistreManuel,
                        numeroSinistreLocal: r.numeroSinistreLocal,
                        numeroSinistreEcarteBrune: r.numeroSinistreEcarteBrune,
                        numeroSinistreHomologue: r.numeroSinistreHomologue,
                        dateAccident: r.dateAccident,
                        typeSinistre: r.typeSinistre,
                        typeDommage: r.typeDommage,
                        assureNomComplet: r.assureNomComplet,
                        assureImmatriculation: r.assureImmatriculation,
                        assureurDeclarant: r.assureurDeclarant,
                        numeroPoliceAssureur: r.numeroPoliceAssureur,
                        paysEmetteurCode: r.paysEmetteurCode,
                        paysGestionnaireCode: r.paysGestionnaireCode,
                    })),
                });
                const d = res.data?.data;
                importes += d?.importes || batch.length;
                erreurs += d?.erreurs || 0;
                doublons += d?.doublons || 0;
                // Enrichir chaque détail avec les données source du batch
                if (d?.details) {
                    d.details.forEach(det => {
                        const src = batch.find(r => r.numeroSinistreManuel === det.numero);
                        details.push({
                            ...det,
                            ligne: src ? src._ligne : null,
                            feuille: src ? src._type : null,
                            assure: src ? src.assureNomComplet : null,
                            dateAccident: src ? src.dateAccident : null,
                            paysCode: src ? (src.paysEmetteurCode || src.paysGestionnaireCode) : null,
                            warnings: src ? src._warnings : [],
                        });
                    });
                }
                // Lignes importées avec succès : enregistrer aussi pour la feuille OK
                if (d?.importes > 0) {
                    batch.forEach(r => {
                        const rejected = d?.details?.find(dt => dt.numero === r.numeroSinistreManuel);
                        if (!rejected || rejected.type === 'SINISTRE_CREE' || rejected.type === 'VICTIME_CREEE') {
                            lignesOk.push(r);
                        }
                    });
                }
            } catch (err) {
                erreurs += batch.length;
                details.push({ type: 'ERROR', message: `Batch ${i}-${i + BATCH} : ${err?.response?.data?.message || err.message}` });
            }
            setImportProgress(Math.round(((i + BATCH) / all.length) * 100));
        }

        setRapport({
            totalTraite: all.length,
            totalET: dataET.length,
            totalTE: dataTE.length,
            importes, erreurs, doublons,
            details,
            lignesOk,
            annee: anneeSelectionnee,
            date: new Date().toLocaleString(localeIntl()),
        });
        setImporting(false);
        setStep(3);
    };

    //  Import compagnies 
    const importerCompagnies = async () => {
        setImportingCompagnies(true);
        try {
            const res = await axiosInstance.post('/v1/reprise/organismes', { organismes: compagnies });
            const d = res.data?.data;
            setRapportCompagnies(d);
            toast.success(t('sinistres.compagniesImportees', { nombre: d?.importes || compagnies.length }));
        } catch (err) {
            toast.error(err?.response?.data?.message || t('sinistres.erreurImportCompagnies'));
        } finally {
            setImportingCompagnies(false);
        }
    };

    //  Mapping type → raison + action corrective 
    const RAISONS = {
        DOUBLON: t('sinistres.raisons.DOUBLON'),
        ERROR_PAYS: t('sinistres.raisons.ERROR_PAYS'),
        ERROR_ORG: t('sinistres.raisons.ERROR_ORG'),
        ERROR_DATE: t('sinistres.raisons.ERROR_DATE'),
        ERROR_SINISTRE: t('sinistres.raisons.ERROR_SINISTRE'),
        ERROR_OTHER: t('sinistres.raisons.ERROR_OTHER'),
        SINISTRE_CREE: t('sinistres.raisons.SINISTRE_CREE'),
        VICTIME_CREEE: t('sinistres.raisons.VICTIME_CREEE'),
        NUMERO_SINISTRE_CORRIGE: t('sinistres.raisons.NUMERO_SINISTRE_CORRIGE'),
    };
    const ACTIONS = {
        DOUBLON: t('sinistres.actions.DOUBLON'),
        ERROR_PAYS: t('sinistres.actions.ERROR_PAYS'),
        ERROR_ORG: t('sinistres.actions.ERROR_ORG'),
        ERROR_DATE: t('sinistres.actions.ERROR_DATE'),
        ERROR_SINISTRE: t('sinistres.actions.ERROR_SINISTRE'),
        ERROR_OTHER: t('sinistres.actions.ERROR_OTHER'),
        SINISTRE_CREE: t('sinistres.actions.SINISTRE_CREE'),
        VICTIME_CREEE: t('sinistres.actions.VICTIME_CREEE'),
        NUMERO_SINISTRE_CORRIGE: t('sinistres.actions.NUMERO_SINISTRE_CORRIGE'),
    };

    const categoriserErreur = (d) => {
        if (d.type === 'DOUBLON') return 'DOUBLON';
        if (d.type === 'SINISTRE_CREE') return 'SINISTRE_CREE';
        if (d.type === 'VICTIME_CREEE') return 'VICTIME_CREEE';
        if (d.type === 'NUMERO_SINISTRE_CORRIGE') return 'NUMERO_SINISTRE_CORRIGE';
        const msg = (d.message || '').toLowerCase();
        if (msg.includes('pays')) return 'ERROR_PAYS';
        if (msg.includes('organisme') || msg.includes('compagnie') || msg.includes('introuvable')) return 'ERROR_ORG';
        if (msg.includes('date') || msg.includes('format')) return 'ERROR_DATE';
        if (msg.includes('sinistre')) return 'ERROR_SINISTRE';
        return 'ERROR_OTHER';
    };

    //  Export rapport - 4 feuilles 
    const exporterRapport = async () => {
        if (!rapport) return;
        const wb = XLSX.utils.book_new();
        const now = new Date().toLocaleString(localeIntl());
        const x = (cle, options) => t(`sinistres.export.${cle}`, options);
        const annee = rapport.annee || anneeSelectionnee;

        //  FEUILLE 1 : Résumé 
        const resume = [
            [x('titreResume', { annee }), '', '', ''],
            [x('genereLe'), now, '', ''],
            ['', '', '', ''],
            [x('synthese'), x('nombre'), x('pctTotal'), x('statut')],
            [x('totalLignesTraitees'), rapport.totalTraite, '100%', ''],
            [x('sinistresEt'), rapport.totalET, `${rapport.totalTraite ? ((rapport.totalET / rapport.totalTraite) * 100).toFixed(1) : 0}%`, ''],
            [x('sinistresTe'), rapport.totalTE, `${rapport.totalTraite ? ((rapport.totalTE / rapport.totalTraite) * 100).toFixed(1) : 0}%`, ''],
            ['', '', '', ''],
            [x('importesSucces'), rapport.importes, `${rapport.totalTraite ? ((rapport.importes / rapport.totalTraite) * 100).toFixed(1) : 0}%`, 'OK'],
            [x('doublonsIgnores'), rapport.doublons, `${rapport.totalTraite ? ((rapport.doublons / rapport.totalTraite) * 100).toFixed(1) : 0}%`, x('ignore')],
            [x('creationsAuto'),
                rapport.details?.filter(d => d.type === 'SINISTRE_CREE' || d.type === 'VICTIME_CREEE').length || 0, '', x('aCompleter')],
            [x('numerosCorriges'),
                rapport.details?.filter(d => d.type === 'NUMERO_SINISTRE_CORRIGE').length || 0, '', 'AUTO'],
            [x('erreursRejetees'), rapport.erreurs, `${rapport.totalTraite ? ((rapport.erreurs / rapport.totalTraite) * 100).toFixed(1) : 0}%`, x('rejete')],
            ['', '', '', ''],
            [x('legende'), '', '', ''],
            [x('type'), x('signification'), x('actionRecommandee'), ''],
            ...Object.entries(RAISONS).map(([k, v]) => [k, v, ACTIONS[k], '']),
        ];
        const wsResume = XLSX.utils.aoa_to_sheet(resume);
        wsResume['!cols'] = [{ wch: 28 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, wsResume, x('feuilles.resume'));

        //  FEUILLE 2 : Lignes importées OK -
        const okRows = [
            [x('titreImportes', { annee })],
            [],
            [x('ligneExcel'), x('feuille'), x('numeroSinistreManuel'), x('numeroLocal'), x('dateAccident'),
                x('type'), x('dommage'), x('assure'), x('immatriculation'), x('paysEmetteur'), x('avertissementsParsing')],
        ];
        (rapport.lignesOk || []).forEach(r => {
            okRows.push([
                r._ligne || '', r._type || '',
                r.numeroSinistreManuel || '', r.numeroSinistreLocal || '',
                r.dateAccident || '', r.typeSinistre || '', r.typeDommage || '',
                r.assureNomComplet || '', r.assureImmatriculation || '',
                r.paysEmetteurCode || r.paysGestionnaireCode || '',
                (r._warnings || []).join(' | ') || '',
            ]);
        });
        const wsOk = XLSX.utils.aoa_to_sheet(okRows);
        wsOk['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 22 }, { wch: 18 }, { wch: 13 }, { wch: 10 }, { wch: 10 }, { wch: 28 }, { wch: 16 }, { wch: 8 }, { wch: 35 }];
        XLSX.utils.book_append_sheet(wb, wsOk, x('feuilles.importes'));

        //  FEUILLE 3 : Anomalies (doublons + erreurs) 
        const anomalies = (rapport.details || []).filter(d =>
            d.type === 'DOUBLON' || d.type === 'ERROR'
        );
        const anomRows = [
            [x('titreAnomalies', { annee })],
            [],
            [x('ligneExcel'), x('feuille'), x('numeroSinistre'), x('assure'), x('dateAccident'), x('pays'),
                x('typeAnomalie'), x('raisonDetaillee'), x('actionCorrective'), x('messageTechnique')],
        ];
        anomalies.forEach(d => {
            const cat = categoriserErreur(d);
            anomRows.push([
                d.ligne || '', d.feuille || '',
                d.numero || '',
                d.assure || '',
                d.dateAccident || '',
                d.paysCode || '',
                d.type,
                RAISONS[cat] || d.message,
                ACTIONS[cat] || t('commun.interventionManuelle'),
                d.message || '',
            ]);
        });
        const wsAnom = XLSX.utils.aoa_to_sheet(anomRows);
        wsAnom['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 22 }, { wch: 28 }, { wch: 13 }, { wch: 8 }, { wch: 12 }, { wch: 40 }, { wch: 50 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(wb, wsAnom, x('feuilles.anomalies'));

        //  FEUILLE 4 : Créations automatiques -
        const autos = (rapport.details || []).filter(d =>
            d.type === 'SINISTRE_CREE' || d.type === 'VICTIME_CREEE'
        );
        const autoRows = [
            [x('titreCreations', { annee })],
            [x('creationsExplication1'), ``, ``, ``],
            [x('creationsExplication2')],
            [],
            [x('ligneExcel'), x('feuille'), x('numeroReference'), x('typeCreation'), x('ceQuiAEteCree'), x('actionAMener')],
        ];
        autos.forEach(d => {
            const cat = categoriserErreur(d);
            autoRows.push([
                d.ligne || '', d.feuille || '',
                d.numero || '',
                d.type,
                d.message || '',
                x('ouvrirEtCompleter'),
            ]);
        });
        const wsAuto = XLSX.utils.aoa_to_sheet(autoRows);
        wsAuto['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 22 }, { wch: 16 }, { wch: 55 }, { wch: 55 }];
        XLSX.utils.book_append_sheet(wb, wsAuto, x('feuilles.creations'));

        //  FEUILLE 5 : Avertissements parsing -
        const averts = [...(rapport.lignesOk || []), ...(rapport.details || [])]
            .filter(r => r._warnings?.length > 0 || r.warnings?.length > 0);
        if (averts.length > 0) {
            const avertRows = [
                [x('titreAvertissements', { annee })],
                [x('avertissementsExplication')],
                [],
                [x('ligneExcel'), x('feuille'), x('numeroSinistre'), x('assure'), x('avertissementsDetectes'), x('impactPossible')],
            ];
            averts.forEach(r => {
                const warns = r._warnings || r.warnings || [];
                const impact = warns.map(w => {
                    // Avertissements produits par valider() : reconnus par leur texte
                    // traduit, et non par un mot français, pour valoir dans les deux langues.
                    if (w === avert('dateManquante')) return x('impacts.date');
                    if (w === avert('assureManquant')) return x('impacts.assure');
                    if (w === avert('paysEmetteurManquant')) return x('impacts.pays');
                    if (w.startsWith(avert('dommageInconnu', { type: '' }))) return x('impacts.dommage');
                    return x('impacts.autre');
                }).join(' | ');
                avertRows.push([
                    r._ligne || r.ligne || '', r._type || r.feuille || '',
                    r.numeroSinistreManuel || r.numero || '',
                    r.assureNomComplet || r.assure || '',
                    warns.join(' | '),
                    impact,
                ]);
            });
            const wsAvert = XLSX.utils.aoa_to_sheet(avertRows);
            wsAvert['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 22 }, { wch: 28 }, { wch: 50 }, { wch: 50 }];
            XLSX.utils.book_append_sheet(wb, wsAvert, x('feuilles.avertissements'));
        }

        //  FEUILLE 6 : Numéros de sinistre corrigés (nomenclature) 
        const corriges = (rapport.details || []).filter(d => d.type === 'NUMERO_SINISTRE_CORRIGE');
        if (corriges.length > 0) {
            const corrRows = [
                [x('titreCorriges', { annee })],
                [x('corrigesExplication1')],
                [x('corrigesExplication2')],
                [],
                [x('ligneExcel'), x('numeroEnregistre'), x('correctionAppliquee')],
            ];
            corriges.forEach(d => {
                corrRows.push([d.ligne || '-', d.numero || '', d.message || '']);
            });
            const wsCorr = XLSX.utils.aoa_to_sheet(corrRows);
            wsCorr['!cols'] = [{ wch: 10 }, { wch: 24 }, { wch: 70 }];
            XLSX.utils.book_append_sheet(wb, wsCorr, x('feuilles.corriges'));
        }

        const now2 = new Date();
        const suffix = `${now2.toLocaleDateString('fr-FR').replace(/\//g, '-')}_${now2.toLocaleTimeString('fr-FR').replace(/:/g, 'h')}_by_CBConnect`;
        await XLSX.writeFile(wb, `Rapport_Reprise_Sinistres_${annee}_${suffix}.xlsx`);
    };

    // ════════════════════════════════════════════════════════════════════════
    //  RENDU
    // ════════════════════════════════════════════════════════════════════════
    return (
        <Layout title={t('sinistres.titre')}>
            <div style={{ maxWidth: 1400 }}>

                {/*  Overlay parsing fichier  */}
                {parsingFile && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 2000,
                        background: 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(3px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <div style={{
                            background: '#fff', borderRadius: 16,
                            padding: '36px 48px', textAlign: 'center',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
                            minWidth: 320,
                        }}>
                            <div style={{ fontSize: 36, marginBottom: 16 }}><BarChartOutlined style={{ color: '#1a6b3c' }} /></div>
                            <div style={{
                                fontSize: 16, fontWeight: 600, color: '#0f172a',
                                marginBottom: 6,
                            }}>
                                {t('sinistres.analyseEnCours')}
                            </div>
                            <div style={{
                                fontSize: 13, color: '#94a3b8', marginBottom: 24,
                            }}>
                                {fileName}
                            </div>
                            <Progress
                                percent={fakeProgress}
                                status={fakeProgress < 100 ? 'active' : 'success'}
                                strokeColor={{ '0%': '#1a6b3c', '100%': '#4ade80' }}
                                trailColor="#e2e8f0"
                                strokeWidth={8}
                                style={{ width: 260 }}
                            />
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
                                {t('sinistres.lectureFeuilles', { annee: anneeSelectionnee })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Assistant d'import : pas de `compte`, l'ecran demarre vide et un
                    « 0 sinistre » se lirait comme une liste vide, pas comme un import
                    a demarrer. */}
                <PageHeader
                    titre={t('sinistres.titre')}
                    icone={<CloudUploadOutlined />}
                    actions={(
                        <Space>
                            <RecalculStatutsButton />
                            <Select
                                value={anneeSelectionnee}
                                onChange={(v) => { setAnneeSelectionnee(v); setStep(0); setDataET([]); setDataTE([]); }}
                                style={{ width: 100, borderRadius: 8 }}
                                options={Array.from({ length: 10 }, (_, i) => {
                                    const a = new Date().getFullYear() - i;
                                    return { value: a, label: String(a) };
                                })}
                            />
                            {compagnies.length > 0 && (
                                <Button
                                    icon={<CloudUploadOutlined />}
                                    style={{ borderColor: '#1a6b3c', color: '#1a6b3c', borderRadius: 8 }}
                                    onClick={() => setShowCompagniesModal(true)}
                                >
                                    {t('sinistres.importerCompagniesCedeao', { nombre: compagnies.length })}
                                </Button>
                            )}
                        </Space>
                    )}
                />

                {/*  Étapes  */}
                <div className="cb-card" style={{ marginBottom: 24 }}>
                    <Steps current={step} size="small" items={[
                        { title: t('commun.etapes.fichierExcel'), icon: <FileExcelOutlined /> },
                        { title: t('sinistres.etapes.previsualisation') },
                        { title: t('sinistres.etapes.confirmation') },
                        { title: t('sinistres.etapes.rapport'), icon: <BarChartOutlined /> },
                    ]} />
                </div>

                {/*  ÉTAPE 0 : Upload  */}
                {step === 0 && (
                    <div className="cb-card" style={{ textAlign: 'center', padding: 40 }}>
                        <Title level={4} style={{ color: '#1a6b3c', marginBottom: 8 }}>
                            <FolderOpenOutlined /> {t('sinistres.charger')}
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                            <Trans t={t} i18nKey="sinistres.consigneFichier" values={{ annee: anneeSelectionnee }}
                                components={{ strong: <strong />, br: <br /> }} />
                        </Text>

                        <Dragger
                            accept=".xlsx,.xls"
                            beforeUpload={onExcelDrop}
                            showUploadList={false}
                            style={{ maxWidth: 600, margin: '0 auto', borderColor: '#1a6b3c', borderRadius: 12 }}
                        >
                            <p><InboxOutlined style={{ fontSize: 48, color: '#1a6b3c' }} /></p>
                            <p style={{ fontSize: 15, fontWeight: 600 }}>{t('commun.glisserDeposer')}</p>
                            <p style={{ color: '#6b7280', fontSize: 13 }}>{t('commun.ouCliquer')}</p>
                        </Dragger>

                        <Divider style={{ margin: '32px 0 16px' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('sinistres.ouCompagnies')}</Text>
                        </Divider>

                        <Dragger
                            accept=".xlsx,.xls"
                            beforeUpload={onCompagniesDrop}
                            showUploadList={false}
                            style={{ maxWidth: 400, margin: '0 auto', borderColor: '#d5c93b', borderRadius: 10, background: '#fdfce8' }}
                        >
                            <p><FileExcelOutlined style={{ fontSize: 28, color: '#d5c93b' }} /></p>
                            <p style={{ fontSize: 13 }}>{t('sinistres.fichierCompagnies')}</p>
                        </Dragger>
                    </div>
                )}

                {/*  ÉTAPE 1 : Preview  */}
                {step === 1 && (
                    <>
                        {/* Stats globales */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            {[
                                { title: t('sinistres.totalSinistres'), value: stats.total, color: '#1a6b3c', bg: '#e8f5ed' },
                                { title: t('sinistres.sinistresEt'), value: stats.totalET, color: '#2980b9', bg: '#dbeafe', sub: t('sinistres.survenuTogo') },
                                { title: t('sinistres.sinistresTe'), value: stats.totalTE, color: '#8e44ad', bg: '#f3e8ff', sub: t('sinistres.survenuEtranger') },
                                { title: t('commun.valides'), value: stats.valid, color: '#166534', bg: '#dcfce7' },
                                { title: t('sinistres.avecAlertes'), value: stats.warnings, color: '#92400e', bg: '#fef3c7' },
                            ].map((s, i) => (
                                <Col xs={12} md={5} key={i}>
                                    <div style={{ background: s.bg, borderRadius: 12, padding: '14px 18px' }}>
                                        <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: 12, color: '#374151' }}>{s.title}</div>
                                        {s.sub && <div style={{ fontSize: 11, color: '#6b7280' }}>{s.sub}</div>}
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {stats.warnings > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                icon={<ExclamationCircleOutlined />}
                                message={t('sinistres.lignesAvecAlertes', { count: stats.warnings })}
                                description={t('sinistres.lignesImportees')}
                                style={{ marginBottom: 16, borderRadius: 10 }}
                            />
                        )}

                        {/* Tableau preview */}
                        <div className="cb-card" style={{ padding: 0 }}>
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                tabBarStyle={{ padding: '0 24px', marginBottom: 0 }}
                                tabBarExtraContent={
                                    <div style={{ paddingRight: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <Tag color="geekblue" style={{ borderRadius: 20 }}>{t('sinistres.fichier', { fichier: fileName })}</Tag>
                                    </div>
                                }
                                items={[
                                    {
                                        key: 'ET',
                                        label: <span><Badge count={dataET.filter(r => r._hasWarning).length} size="small" offset={[6, -2]}>
                                            <GlobalOutlined /> {t('sinistres.ongletEt', { nombre: dataET.length })}
                                        </Badge></span>,
                                        children: (
                                            <Table
                                                className="cb-table"
                                                dataSource={dataET}
                                                columns={COLS_PREVIEW(t)}
                                                rowKey={(r, i) => `et-${i}`}
                                                size="small"
                                                scroll={{ x: 1200, y: 400 }}
                                                pagination={{
                                                    pageSize: 50, showSizeChanger: false,
                                                    showTotal: (n) => t('sinistres.nbSinistresEt', { nombre: n })
                                                }}
                                                rowClassName={r => r._hasWarning ? 'ant-table-row-warning' : ''}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'TE',
                                        label: <span><Badge count={dataTE.filter(r => r._hasWarning).length} size="small" offset={[6, -2]}>
                                            <SendOutlined /> {t('sinistres.ongletTe', { nombre: dataTE.length })}
                                        </Badge></span>,
                                        children: (
                                            <Table
                                                className="cb-table"
                                                dataSource={dataTE}
                                                columns={COLS_PREVIEW(t)}
                                                rowKey={(r, i) => `te-${i}`}
                                                size="small"
                                                scroll={{ x: 1200, y: 400 }}
                                                pagination={{
                                                    pageSize: 50, showSizeChanger: false,
                                                    showTotal: (n) => t('sinistres.nbSinistresTe', { nombre: n })
                                                }}
                                                rowClassName={r => r._hasWarning ? 'ant-table-row-warning' : ''}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'repartition',
                                        label: <span><BarChartOutlined /> {t('sinistres.repartitionPays')}</span>,
                                        children: (
                                            <div style={{ padding: 24 }}>
                                                <Row gutter={[12, 12]}>
                                                    {Object.entries(stats.byPays)
                                                        .sort((a, b) => b[1] - a[1])
                                                        .map(([pays, count]) => (
                                                            <Col xs={12} md={6} lg={4} key={pays}>
                                                                <div style={{
                                                                    background: '#f0faf4', borderRadius: 8,
                                                                    padding: '12px 16px', border: '1px solid #b7e0c9'
                                                                }}>
                                                                    <div style={{ fontSize: 22, fontWeight: 800, color: '#1a6b3c' }}>{count}</div>
                                                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{pays}</div>
                                                                    <div style={{ fontSize: 11, color: '#6b7280' }}>{PAYS_LIBELLE[pays] || pays}</div>
                                                                </div>
                                                            </Col>
                                                        ))}
                                                </Row>
                                            </div>
                                        ),
                                    },
                                ]}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
                            <Button onClick={() => { setStep(0); setDataET([]); setDataTE([]); }} style={{ borderRadius: 8 }}>
                                {t('sinistres.rechargerFichier')}
                            </Button>
                            <Button type="primary" onClick={() => setStep(2)} style={{
                                background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8,
                                fontSize: 15, height: 44, paddingInline: 28,
                            }}>
                                {t('commun.passerConfirmation')}
                            </Button>
                        </div>
                    </>
                )}

                {/*  ÉTAPE 2 : Confirmation  */}
                {step === 2 && (
                    <div className="cb-card" style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', padding: 40 }}>
                        <Title level={3} style={{ color: '#1a6b3c', marginBottom: 8 }}>
                            <CheckCircleOutlined /> {t('commun.confirmerImport')}
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 32 }}>
                            <Trans t={t} i18nKey="sinistres.consigneConfirmation" values={{ nombre: stats.total }}
                                components={{ strong: <strong />, br: <br /> }} />
                        </Text>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32, textAlign: 'left' }}>
                            {[
                                { label: t('sinistres.sinistresEtSurvenu'), icon: <GlobalOutlined />, value: stats.totalET, bg: '#dbeafe', color: '#1e40af' },
                                { label: t('sinistres.sinistresTeSurvenu'), icon: <SendOutlined />, value: stats.totalTE, bg: '#f3e8ff', color: '#7c3aed' },
                                { label: t('sinistres.lignesValides'), icon: <CheckCircleOutlined />, value: stats.valid, bg: '#dcfce7', color: '#166534' },
                                { label: t('sinistres.lignesAlertes'), icon: <WarningOutlined />, value: stats.warnings, bg: '#fef3c7', color: '#92400e' },
                            ].map((s, i) => (
                                <div key={i} style={{ background: s.bg, borderRadius: 10, padding: '14px 18px' }}>
                                    <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                                    <div style={{ fontSize: 13, color: '#374151' }}>{s.icon} {s.label}</div>
                                </div>
                            ))}
                        </div>

                        {importing ? (
                            <div style={{ marginBottom: 24 }}>
                                <Progress percent={importProgress} status="active" strokeColor="#1a6b3c" />
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    {t('sinistres.importEnCours', { fait: Math.round(importProgress * stats.total / 100), total: stats.total })}
                                </Text>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                                <Button onClick={() => setStep(1)} style={{ borderRadius: 8 }} disabled={importing}>
                                    {t('sinistres.retourPreview')}
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<CloudUploadOutlined />}
                                    onClick={lancerImport}
                                    loading={importing}
                                    style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, paddingInline: 32 }}
                                >
                                    {t('sinistres.lancerImport', { nombre: stats.total })}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/*  ÉTAPE 3 : Rapport  */}
                {step === 3 && rapport && (
                    <>
                        <Result
                            icon={rapport.erreurs === 0
                                ? <CheckCircleOutlined style={{ color: '#1a6b3c' }} />
                                : <WarningOutlined style={{ color: '#f97316' }} />}
                            status={rapport.erreurs === 0 ? 'success' : 'warning'}
                            title={rapport.erreurs === 0
                                ? t('sinistres.importTermine', { nombre: rapport.importes })
                                : t('sinistres.importTermineErreurs', { count: rapport.erreurs })}
                            subTitle={t('sinistres.sousTitreRapport', { annee: anneeSelectionnee, date: rapport.date })}
                        />

                        {/* Rapport chiffré */}
                        <div className="cb-card" style={{ maxWidth: 800, margin: '0 auto 20px' }}>
                            <Title level={4} style={{ color: '#1a6b3c', marginBottom: 20 }}>
                                <BarChartOutlined /> {t('sinistres.titreRapport', { annee: anneeSelectionnee })}
                            </Title>

                            <Row gutter={[20, 20]}>
                                {[
                                    { title: t('commun.totalTraite'), icon: null, value: rapport.totalTraite, color: '#374151', bg: '#f4f6f5' },
                                    { title: t('sinistres.sinistresEt'), icon: <GlobalOutlined />, value: rapport.totalET, color: '#1e40af', bg: '#dbeafe' },
                                    { title: t('sinistres.sinistresTe'), icon: <SendOutlined />, value: rapport.totalTE, color: '#7c3aed', bg: '#f3e8ff' },
                                    { title: t('commun.importes'), icon: <CheckCircleOutlined />, value: rapport.importes, color: '#166534', bg: '#dcfce7' },
                                    { title: t('sinistres.doublons'), icon: <SyncOutlined />, value: rapport.doublons, color: '#92400e', bg: '#fef3c7' },
                                    { title: t('commun.erreurs'), icon: <CloseCircleOutlined />, value: rapport.erreurs, color: '#991b1b', bg: '#fee2e2' },
                                ].map((s, i) => (
                                    <Col xs={12} md={8} key={i}>
                                        <div style={{
                                            background: s.bg, borderRadius: 10, padding: '16px 20px',
                                            borderLeft: `4px solid ${s.color}`
                                        }}>
                                            <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>{s.value}</div>
                                            <div style={{ fontSize: 13 }}>{s.icon} {s.title}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {rapport.details?.length > 0 && (
                                <div style={{ marginTop: 20 }}>
                                    <Divider>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('commun.anomaliesLignes', { count: rapport.details.length })}
                                            {rapport.details.length > 50 && t('sinistres.cinquantePremieres')}
                                        </Text>
                                    </Divider>
                                    <Table
                                        size="small"
                                        scroll={{ x: 900 }}
                                        dataSource={rapport.details.slice(0, 50).map((d, i) => ({
                                            ...d,
                                            key: i,
                                            _categorie: categoriserErreur(d),
                                        }))}
                                        columns={[
                                            {
                                                title: t('commun.colonnes.ligne'), dataIndex: 'ligne', width: 60,
                                                render: (v, r) => v
                                                    ? <span style={{ fontFamily: 'var(--font-main)', fontSize: 11 }}>{r.feuille} L{v}</span>
                                                    : '-',
                                            },
                                            {
                                                title: t('commun.colonnes.type'), dataIndex: 'type', width: 110,
                                                render: v => {
                                                    const colors = { ERROR: 'red', DOUBLON: 'orange', SINISTRE_CREE: 'blue', VICTIME_CREEE: 'cyan', NUMERO_SINISTRE_CORRIGE: 'geekblue' };
                                                    return <Tag color={colors[v] || 'default'} style={{ borderRadius: 20, fontSize: 10 }}>{v}</Tag>;
                                                },
                                            },
                                            {
                                                title: t('commun.colonnes.numeroSinistre'), dataIndex: 'numero', width: 170,
                                                render: v => <code style={{ fontSize: 11 }}>{v || '-'}</code>,
                                            },
                                            {
                                                title: t('commun.colonnes.assure'), dataIndex: 'assure', width: 150,
                                                ellipsis: true, render: v => v || '-'
                                            },
                                            {
                                                title: t('commun.colonnes.raison'), key: 'raison', ellipsis: true,
                                                render: (_, r) => {
                                                    const cat = r._categorie || categoriserErreur(r);
                                                    return <span style={{ fontSize: 12, color: '#374151' }}>
                                                        {RAISONS[cat] || r.message}
                                                    </span>;
                                                },
                                            },
                                            {
                                                title: t('commun.colonnes.actionCorrective'), key: 'action', width: 260, ellipsis: true,
                                                render: (_, r) => {
                                                    const cat = r._categorie || categoriserErreur(r);
                                                    const isWarn = cat === 'SINISTRE_CREE' || cat === 'VICTIME_CREEE' || cat === 'NUMERO_SINISTRE_CORRIGE';
                                                    return <span style={{
                                                        fontSize: 11,
                                                        color: isWarn ? '#92400e' : r.type === 'DOUBLON' ? '#6b7280' : '#991b1b',
                                                    }}>
                                                        {ACTIONS[cat] || t('commun.interventionManuelle')}
                                                    </span>;
                                                },
                                            },
                                        ]}
                                        pagination={{ pageSize: 10, showTotal: (n) => t('commun.nbAnomalies', { count: n }) }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center' }}>
                                <Button icon={<DownloadOutlined />} onClick={exporterRapport} style={{ borderRadius: 8 }}>
                                    {t('commun.exporterRapport')}
                                </Button>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => { setStep(0); setDataET([]); setDataTE([]); setRapport(null); setFileName(''); }}
                                    style={{ borderRadius: 8 }}
                                >
                                    {t('commun.nouvelleReprise')}
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {/*  Modal Compagnies  */}
                <Modal
                    title={<span><GlobalOutlined /> {t('sinistres.modaleCompagnies.titre', { nombre: compagnies.length })}</span>}
                    open={showCompagniesModal}
                    onCancel={() => setShowCompagniesModal(false)}
                    footer={null}
                    width={700}
                >
                    <Alert type="info" showIcon message={t('sinistres.modaleCompagnies.info')}
                        description={t('sinistres.modaleCompagnies.doublons')} style={{ marginBottom: 16, borderRadius: 8 }} />

                    <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}>
                        <Table
                            size="small"
                            dataSource={compagnies.map((c, i) => ({ ...c, key: i }))}
                            columns={[
                                {
                                    title: t('sinistres.modaleCompagnies.code'), dataIndex: 'code', width: 110,
                                    render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>{v}</span>
                                },
                                { title: t('sinistres.modaleCompagnies.raisonSociale'), dataIndex: 'raisonSociale', ellipsis: true },
                                {
                                    title: t('sinistres.modaleCompagnies.pays'), dataIndex: 'pays', width: 100,
                                    render: v => v ? <Tag style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag> : '-'
                                },
                            ]}
                            pagination={false}
                        />
                    </div>

                    {rapportCompagnies && (
                        <Alert type="success" showIcon
                            message={t('sinistres.modaleCompagnies.importTermine', { importees: rapportCompagnies.importes, doublons: rapportCompagnies.doublons || 0 })}
                            style={{ marginBottom: 12, borderRadius: 8 }} />
                    )}

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setShowCompagniesModal(false)}>{t('commun.fermer')}</Button>
                        <Button type="primary" loading={importantCompagnies} onClick={importerCompagnies}
                            style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
                            {t('sinistres.modaleCompagnies.importer', { nombre: compagnies.length })}
                        </Button>
                    </div>
                </Modal>

            </div>

            <style>{`
        .ant-table-row-warning td { background: #fff7ed !important; }
        .ant-table-row-warning:hover td { background: #fef3c7 !important; }
      `}</style>
        </Layout>
    );
}